import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Modal, Input, Alert, Select, Form, Spin, Switch, Row, Radio, Popover } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import SupplierRegionsLocationsTreeSelect from '../regions_tree_select/SupplierRegionsLocationsTreeSelect';
import { fetchUserForSupplier } from '../../thunks/users_thunks';
import {
	hasRequiredRole,
	ROLES,
	SUPPLIER_ADMIN_ONLY,
	SUPPLIER_ASSIGNABLE_ROLES,
	SUPPLIER_LOCATION_RESTRICTED_ROLES,
	SUPPLIER_TECH,
} from '../roles/roles';
import StockLocationFormFields from './StockLocationFormFields';
import BrandSelection from '../brand_selection/BrandSelection';
import { isInternalTech } from '../../utils/AuthUtils';
import { InfoCircleOutlined } from '@ant-design/icons';
import { EPA_CERTIFICATION_TYPE_OPTIONS } from '../../utils/RefrigerantUtils';
import { isRefrigerantTrackingEnabled } from '../../utils/FeatureFlagUtils';

const FormItem = Form.Item;

interface SupplierRoleUpdateFormProps extends FormComponentProps {
	onSubmit: any;
	onCancel: any;
	visible: boolean;
	createErrors: any;
	updateErrors: any;
	creating: boolean;
	updating: boolean;
	currentUser: any;
	formData: any;

	locations: any;
	fetchUser: any;
}

const SupplierRoleUpdateForm: FC<SupplierRoleUpdateFormProps> = ({
	visible,
	onCancel,
	onSubmit,
	formData,
	currentUser,
	createErrors,
	updateErrors,
	locations,
	fetchUser,
	creating,
	updating,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const { setFieldsValue } = form;

	const [user, setUser] = useState();
	const [loading, setLoading] = useState(false);
	const [hasBrands, setHasBrands] = useState(false);

	const email = useMemo(() => nullSafeGet('contact.email', formData), [formData]);
	const roles = Form.useWatch('roles', form);
	const rolesArray: string[] = Array.isArray(roles) ? roles : roles ? [roles] : [];
	const includesSupplierAdmin = hasRequiredRole(rolesArray, SUPPLIER_ADMIN_ONLY);
	const includesSupplierTech = rolesArray.includes(SUPPLIER_TECH);
	const hasLocationRestrictedRole =
		!includesSupplierAdmin && hasRequiredRole(rolesArray, SUPPLIER_LOCATION_RESTRICTED_ROLES);
	const hasAccessToAllLocations = Form.useWatch('hasAccessToAllLocations', form);
	const byLocationOrBrand = Form.useWatch('byLocationOrBrand', form);

	// One read per render, shared by the seed, the submit branch and the fields themselves — all
	// three have to agree, or the modal writes a credential it never showed.
	const refrigerantTrackingEnabled = isRefrigerantTrackingEnabled();

	const toggleAccessType = useMemo(
		() => (value) => {
			setFieldsValue({
				locationIds: [],
				brandIds: [],
				byLocationOrBrand: value,
			});
		},
		[setFieldsValue]
	);

	const refreshUser = useCallback(() => {
		setLoading(true);
		fetchUser(email)
			.then((user) => {
				const locationOrBrand =
					nullSafeGetOrElse('brandIds', user, []).length > 0 ? 'brand' : 'location';
				setUser(user);
				setFieldsValue({
					email: nullSafeGet('contact.email', user),
					nameGiven: nullSafeGet('contact.nameGiven', user),
					nameFamily: nullSafeGet('contact.nameFamily', user),
					roles:
						nullSafeGetOrElse('role', user, []).length > 0
							? nullSafeGet('role', user)
							: ['SUPPLIER_USER'],
					locationIds:
						nullSafeGet('locationIds', user) === undefined ? [] : nullSafeGet('locationIds', user),
					hasAccessToAllLocations: !!user.hasAccessToAllLocations,
					isSharedContact: nullSafeGetOrElse('contact.isSharedContact', user, false),
					readOnlyAccess: nullSafeGetOrElse('contact.readOnlyAccess', user, false),
					brandIds: nullSafeGetOrElse('brandIds', user, []),
					byLocationOrBrand: locationOrBrand,
					// Seeding these two is load-bearing, not cosmetic. The modal submits every registered
					// field on save, so a cert field that opens blank does not merely fail to show the
					// saved value — it saves the blank back over it. An admin editing an unrelated
					// attribute (a role, a location) would silently decertify the tech, and the next
					// refrigerant event would start demanding the manual name/type/number triple with no
					// visible cause. Fetched from the user's nested `contact`, which is where
					// `fetchUserForSupplier` returns them.
					//
					// Behind the flag together with the fields and the submit branch: seeding a field that
					// is never rendered is harmless, but the three have to move as one so the flag-off
					// path neither reads nor writes the credential.
					...(refrigerantTrackingEnabled && {
						epaCertificationType: nullSafeGet('contact.epaCertificationType', user),
						epaCertificationNumber: nullSafeGet('contact.epaCertificationNumber', user),
					}),
				});
			})
			.finally(() => setLoading(false));
	}, [email, fetchUser, setFieldsValue, refrigerantTrackingEnabled]);

	useEffect(() => {
		email && refreshUser();
	}, [email, fetchUser, refreshUser]);

	useEffect(() => {
		setFieldsValue({ supplierFacilityId: nullSafeGet('facility.id', currentUser) });
	}, [currentUser, setFieldsValue]);

	const handleSubmit = useCallback(
		(values) => {
			const submittedRoles: string[] = Array.isArray(values.roles)
				? values.roles
				: values.roles
				? [values.roles]
				: [];
			const submittedIncludesAdmin = hasRequiredRole(submittedRoles, SUPPLIER_ADMIN_ONLY);
			const submittedHasRestrictedRole = hasRequiredRole(
				submittedRoles,
				SUPPLIER_LOCATION_RESTRICTED_ROLES
			);
			const hasAccessToAllLocations =
				submittedIncludesAdmin || !submittedHasRestrictedRole
					? true
					: nullSafeGetOrElse('hasAccessToAllLocations', values, false);
			const brandIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'location'), 'location')
					? []
					: values.brandIds;
			const locationIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'brand'), 'brand')
					? []
					: values.locationIds;
			const updatedValues = {
				...(user || {}),
				...values,
				hasAccessToAllLocations: hasAccessToAllLocations,
				brandIds: brandIds,
				locationIds: locationIds,
				// Explicit null, never undefined, for a cleared credential. Every contact write path
				// reads an ABSENT cert key as "keep whatever is stored" (EpaCertificationRequestParser),
				// and JSON.stringify erases undefined keys — so an admin clearing a lapsed certification
				// would get a success toast and no change at all.
				//
				// Which is exactly why this whole branch is gated. With the fields unrendered every value
				// here is `undefined`, so an ungated save would send two explicit nulls and an admin with
				// the flag off would wipe a certification they were never shown, just by changing a role.
				...(refrigerantTrackingEnabled && {
					epaCertificationType:
						values.epaCertificationType === undefined ? null : values.epaCertificationType,
					epaCertificationNumber:
						values.epaCertificationNumber === undefined ? null : values.epaCertificationNumber,
				}),
			};

			onSubmit && onSubmit(updatedValues);
		},
		[onSubmit, user, refrigerantTrackingEnabled]
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={'Edit User Information'}
			okText={'Update'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={false}
			confirmLoading={creating || updating}
		>
			{loading && !user ? (
				<Spin />
			) : (
				<Form
					form={form}
					layout="vertical"
					className="supplierForm"
					preserve={false}
					onFinish={handleSubmit}
				>
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					<FormItem label="Email" name="email">
						<Input style={{ maxWidth: 300 }} disabled={true} />
					</FormItem>
					<FormItem
						label={'Name'}
						className={'flex flex-row'}
						required={true}
						rules={[
							{
								required: true,
								message: 'Please input name.',
							},
						]}
					>
						<div className={'flex flex-row'} style={{ marginBottom: -24 }}>
							<FormItem name={'nameGiven'}>
								<Input placeholder="Given name" style={{ maxWidth: 200 }} disabled={false} />
							</FormItem>
							<FormItem name={'nameFamily'}>
								<Input
									placeholder="Family name"
									style={{ maxWidth: 200, marginLeft: 8 }}
									disabled={false}
								/>
							</FormItem>
						</div>
					</FormItem>
					{/*
						Placed here, with name and above roles, because these are attributes of the person's
						contact record rather than of their access — everything below Roles is location/brand
						scoping, and interleaving a credential into that block reads as if the cert grants
						access.

						Shown for every role, not just SUPPLIER_TECH. Rule (a) of the refrigerant event's
						acceptance test resolves `technicianEmail` to a contact and asks only whether that
						contact carries both cert fields; it has no notion of role. Hiding the fields behind
						`includesSupplierTech` (as StockLocationFormFields below is) would make a certified
						supervisor's own events unloggable without the manual triple.

						This is the admin path to the same two columns the tech can edit on their own profile
						(SupplierContactForm) — an admin holding the physical 608 card needs to be able to
						enter it without borrowing the tech's login.
					*/}
					{refrigerantTrackingEnabled ? (
						<>
							<FormItem label="EPA Certification Type" name="epaCertificationType">
								<Select
									placeholder="Select certification type"
									allowClear
									style={{ maxWidth: 300 }}
								>
									{EPA_CERTIFICATION_TYPE_OPTIONS.map((option) => (
										<Select.Option value={option.value} key={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select>
							</FormItem>
							<FormItem label="EPA Certification Number" name="epaCertificationNumber">
								<Input placeholder="Certification number" style={{ maxWidth: 300 }} />
							</FormItem>
						</>
					) : null}
					<FormItem
						label="Roles"
						name="roles"
						required={true}
						rules={[
							{
								required: true,
								message: 'Please select a role',
							},
						]}
					>
						<Select mode="multiple" style={{ minWidth: 200 }}>
							{Array.from(SUPPLIER_ASSIGNABLE_ROLES).map((name, idx) => {
								const entity = ROLES[name];
								return (
									<Select.Option value={name} key={`role-${idx}`}>
										{entity.displayName}
									</Select.Option>
								);
							})}
						</Select>
					</FormItem>
					<FormItem
						name="hasAccessToAllLocations"
						valuePropName="checked"
						hidden={!hasLocationRestrictedRole}
					>
						<Row>
							<Switch
								checked={!!form.getFieldValue('hasAccessToAllLocations')}
								onChange={(checked) => form.setFieldsValue({ hasAccessToAllLocations: !!checked })}
							/>
							<label style={{ marginLeft: 8 }}>Select All Locations</label>
						</Row>
					</FormItem>
					<FormItem
						name="byLocationOrBrand"
						required={!hasAccessToAllLocations && hasBrands}
						hidden={
							hasAccessToAllLocations ||
							!hasBrands ||
							!isInternalTech(currentUser) ||
							!hasLocationRestrictedRole
						}
						label={'Restrict Access By'}
					>
						<Radio.Group
							onChange={(e) => {
								toggleAccessType(e.target.value);
							}}
							value={form.getFieldValue('byLocationOrBrand')}
						>
							<Radio.Button value="location">Location</Radio.Button>
							<Radio.Button value="brand">Brand</Radio.Button>
						</Radio.Group>
					</FormItem>
					<FormItem
						name="brandIds"
						hidden={
							includesSupplierAdmin ||
							hasAccessToAllLocations ||
							!compareStrings(byLocationOrBrand, 'brand') ||
							!isInternalTech(currentUser) ||
							!hasLocationRestrictedRole
						}
						required={true}
						label={
							<Row>
								<span>Brands</span>
								<Popover
									placement={'topLeft'}
									content={
										'Selecting a brand will give the user access to all locations in that brand.'
									}
								>
									<InfoCircleOutlined
										style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}
									/>
								</Popover>
							</Row>
						}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'brand'),
								message: 'Please select a brand',
							},
						]}
					>
						<BrandSelection setHasBrands={setHasBrands} />
					</FormItem>
					<FormItem
						name="locationIds"
						hidden={
							includesSupplierAdmin ||
							hasAccessToAllLocations ||
							(isInternalTech(currentUser)
								? !compareStrings(byLocationOrBrand, 'location')
								: false) ||
							!hasLocationRestrictedRole
						}
						required={true}
						label={'Locations'}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'location'),
								message: 'Please select a location',
							},
						]}
					>
						<SupplierRegionsLocationsTreeSelect
							mode="multiple"
							disabled={hasAccessToAllLocations}
							filtersSlice={locations}
							filtersValueAccessor={() => form.getFieldValue('locationIds')}
							targetCollectionName={'buyerUsersLocations'}
						/>
					</FormItem>
					{includesSupplierTech ? (
						<StockLocationFormFields user={user} setFieldsValue={setFieldsValue} />
					) : null}
					<div className="flex flex-row items-baseline">
						<FormItem name="isSharedContact" valuePropName="checked">
							<Switch />
						</FormItem>
						<label className="ml-2">Shared Account</label>
					</div>
					{!includesSupplierAdmin && (
						<div className="flex flex-row items-baseline">
							<FormItem name="readOnlyAccess" valuePropName="checked">
								<Switch />
							</FormItem>
							<label className="ml-2">Read Only Access</label>
						</div>
					)}
				</Form>
			)}
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.supplier_contacts.createErrors,
	updateErrors: state.supplier_contacts.updateErrors,
	creating: state.supplier_contacts.creating,
	updating: state.supplier_contacts.updating,
	currentUser: state.session.currentUser,
	formData: ownProps.formData,

	locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUser: (email) => dispatch(fetchUserForSupplier(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierRoleUpdateForm);
